import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, Clock, CheckCircle, AlertTriangle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const QuizTake = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attemptId, setAttemptId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [result, setResult] = useState(null);
  const [attemptsExhausted, setAttemptsExhausted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    startQuiz();
  }, [quizId]);

  // Timer
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || result) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeLeft, result]);

  const startQuiz = async () => {
    try {
      // Primero obtener el quiz
      const quizRes = await axios.get(`${API_URL}/quizzes/${quizId}`);
      setQuiz(quizRes.data);
      
      // Verificar si ya tiene intentos completados y mostrar el último resultado
      const completedAttempts = quizRes.data.my_attempts?.filter(
        a => a.status === 'completado' || a.status === 'calificado'
      ) || [];
      
      // Verificar si ha agotado los intentos
      const totalAttempts = quizRes.data.my_attempts?.length || 0;
      const allowedAttempts = quizRes.data.intentos_permitidos || 1;
      
      if (totalAttempts >= allowedAttempts) {
        // Mostrar el último resultado si existe
        if (completedAttempts.length > 0) {
          const lastAttempt = completedAttempts[0];
          setResult(lastAttempt);
        } else {
          setAttemptsExhausted(true);
          setErrorMessage('Has agotado tus intentos para este quiz');
        }
        setLoading(false);
        return;
      }
      
      // Si tiene un intento completado pero aún puede reintentar, mostrar el último resultado
      if (completedAttempts.length > 0 && !quizRes.data.my_attempts?.some(a => a.status === 'en_progreso')) {
        const lastAttempt = completedAttempts[0];
        setResult(lastAttempt);
        setLoading(false);
        return;
      }
      
      // Iniciar intento
      const startRes = await axios.post(`${API_URL}/quizzes/${quizId}/start`);
      setAttemptId(startRes.data.attempt_id);
      
      // Configurar timer si hay límite
      if (quizRes.data.tiempo_limite) {
        setTimeLeft(quizRes.data.tiempo_limite * 60);
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Error al cargar el quiz';
      
      if (error.response?.status === 400) {
        // Límite de intentos alcanzado - mostrar estado en lugar de navegar
        setAttemptsExhausted(true);
        setErrorMessage(errorDetail);
        toast.error(errorDetail);
      } else {
        toast.error(errorDetail);
        setErrorMessage(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value, isMultiple = false) => {
    setAnswers(prev => {
      if (isMultiple) {
        const current = prev[questionId]?.respuestas_multiples || [];
        const newMultiple = current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value];
        return {
          ...prev,
          [questionId]: { ...prev[questionId], respuestas_multiples: newMultiple }
        };
      }
      return {
        ...prev,
        [questionId]: { ...prev[questionId], respuesta: value }
      };
    });
  };

  const handleSubmit = async () => {
    if (submitting) return;
    
    const unanswered = quiz.questions.filter(q => !answers[q.id]?.respuesta && !answers[q.id]?.respuestas_multiples?.length);
    if (unanswered.length > 0 && timeLeft > 0) {
      if (!window.confirm(`Hay ${unanswered.length} pregunta(s) sin responder. ¿Enviar de todas formas?`)) {
        return;
      }
    }
    
    setSubmitting(true);
    try {
      const respuestas = quiz.questions.map(q => ({
        question_id: q.id,
        respuesta: answers[q.id]?.respuesta || '',
        respuestas_multiples: answers[q.id]?.respuestas_multiples || []
      }));
      
      const res = await axios.post(`${API_URL}/quizzes/${quizId}/submit`, { 
        attempt_id: attemptId,
        respuestas 
      });
      
      // Usar directamente el resultado del submit
      const submitResult = res.data;
      
      // Construir objeto de resultado compatible con la UI
      const resultData = {
        porcentaje: Math.round(submitResult.puntuacion),
        puntuacion: Math.round(submitResult.puntuacion),
        puntuacion_maxima: 100,
        aprobado: submitResult.aprobado,
        resultados: submitResult.resultados,
        // Añadir las preguntas con sus respuestas para mostrar revisión
        questions: quiz.questions.map(q => {
          const respuestaUsuario = submitResult.resultados?.[q.id];
          return {
            ...q,
            respuesta_alumno: {
              respuesta: respuestaUsuario?.respuesta,
              respuestas_multiples: Array.isArray(respuestaUsuario?.respuesta) ? respuestaUsuario?.respuesta : [],
              es_correcta: respuestaUsuario?.correcto
            }
          };
        })
      };
      
      setResult(resultData);
      toast.success(submitResult.aprobado ? '¡Felicidades! Has aprobado el quiz' : 'Quiz completado');
    } catch (error) {
      console.error('Error al enviar quiz:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  // Show attempts exhausted message
  if (attemptsExhausted && !result) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto space-y-6">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver
          </Button>
          
          <div className="rounded-lg p-8 text-center bg-amber-50 border border-amber-200">
            <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center bg-amber-100">
              <AlertTriangle className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="font-manrope font-bold text-2xl mb-2 text-amber-800">
              Intentos agotados
            </h1>
            <p className="text-slate-600 mb-4">
              {errorMessage || 'Has agotado todos tus intentos para este quiz'}
            </p>
            {quiz && (
              <div className="text-sm text-slate-500 mb-6">
                <p>Quiz: <strong>{quiz.nombre}</strong></p>
                <p>Intentos permitidos: <strong>{quiz.intentos_permitidos || 1}</strong></p>
              </div>
            )}
            <Button onClick={() => navigate(-1)} className="bg-[#004E89] hover:bg-[#003B66]">
              Volver al curso
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Show results
  if (result) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto space-y-6">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver
          </Button>
          
          {/* Result Summary */}
          <div className={`rounded-lg p-8 text-center ${result.aprobado ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${result.aprobado ? 'bg-green-100' : 'bg-red-100'}`}>
              {result.aprobado ? (
                <CheckCircle className="w-10 h-10 text-green-600" />
              ) : (
                <AlertTriangle className="w-10 h-10 text-red-600" />
              )}
            </div>
            <h1 className="font-manrope font-bold text-2xl mb-2">
              {result.aprobado ? '¡Felicidades!' : 'No has aprobado'}
            </h1>
            <p className="text-slate-600 mb-4">
              {result.aprobado ? 'Has superado el quiz correctamente' : 'Puedes intentarlo de nuevo'}
            </p>
            <div className="text-4xl font-bold mb-2" style={{ color: result.aprobado ? '#16a34a' : '#dc2626' }}>
              {result.porcentaje}%
            </div>
            <p className="text-slate-500">
              {result.puntuacion} / {result.puntuacion_maxima} puntos
            </p>
          </div>

          {/* Detailed Results */}
          {quiz?.mostrar_respuestas && result.questions && (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="font-semibold">Revisión de respuestas</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {result.questions.map((q, index) => {
                  const userAnswer = q.respuesta_alumno;
                  const isCorrect = userAnswer?.es_correcta;
                  
                  return (
                    <div key={q.id} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm ${
                          isCorrect ? 'bg-green-500' : 'bg-red-500'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{q.texto}</p>
                          
                          {/* User's answer */}
                          <div className="mt-2 text-sm">
                            <span className="text-slate-500">Tu respuesta: </span>
                            {q.tipo === 'multiple_answer' ? (
                              <span className={isCorrect ? 'text-green-600' : 'text-red-600'}>
                                {userAnswer?.respuestas_multiples?.map(id => {
                                  const opt = q.opciones?.find(o => o.id === id);
                                  return opt?.texto;
                                }).join(', ') || 'Sin respuesta'}
                              </span>
                            ) : (
                              <span className={isCorrect ? 'text-green-600' : 'text-red-600'}>
                                {q.opciones?.find(o => o.id === userAnswer?.respuesta)?.texto || userAnswer?.respuesta || 'Sin respuesta'}
                              </span>
                            )}
                          </div>
                          
                          {/* Correct answer (if wrong) */}
                          {!isCorrect && (
                            <div className="mt-1 text-sm">
                              <span className="text-slate-500">Respuesta correcta: </span>
                              <span className="text-green-600">
                                {q.tipo === 'multiple_answer' 
                                  ? q.opciones?.filter(o => o.es_correcta).map(o => o.texto).join(', ')
                                  : q.respuesta_correcta || q.opciones?.find(o => o.es_correcta)?.texto
                                }
                              </span>
                            </div>
                          )}
                          
                          {/* Explanation */}
                          {q.explicacion && (
                            <div className="mt-2 p-2 bg-slate-50 rounded text-sm text-slate-600">
                              {q.explicacion}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Feedback from instructor */}
          {result.feedback_instructor && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">Comentarios del instructor</h3>
              <p className="text-blue-800">{result.feedback_instructor}</p>
            </div>
          )}

          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate(-1)}>Volver al curso</Button>
            {!result.aprobado && !attemptsExhausted && quiz?.intentos_permitidos > (quiz?.my_attempts?.length || 0) && (
              <Button onClick={() => {
                setResult(null);
                setAttemptId(null);
                setAnswers({});
                setCurrentQuestion(0);
                setLoading(true);
                startQuiz();
              }} className="bg-[#004E89] hover:bg-[#003B66]">
                Intentar de nuevo ({quiz?.intentos_permitidos - (quiz?.my_attempts?.length || 0)} intentos restantes)
              </Button>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  const questions = quiz?.questions || [];
  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="font-manrope font-bold text-xl text-slate-900">{quiz?.titulo}</h1>
          {timeLeft !== null && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
              timeLeft < 60 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'
            }`}>
              <Clock className="w-4 h-4" />
              {formatTime(timeLeft)}
            </div>
          )}
        </div>

        {/* Progress */}
        <div>
          <div className="flex justify-between text-sm text-slate-500 mb-2">
            <span>Pregunta {currentQuestion + 1} de {questions.length}</span>
            <span>{Object.keys(answers).length} respondidas</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question */}
        {question && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <p className="font-medium text-lg text-slate-900 mb-6">{question.texto}</p>

            {/* Multiple choice (single) */}
            {question.tipo === 'multiple_choice' && (
              <RadioGroup
                value={answers[question.id]?.respuesta || ''}
                onValueChange={(v) => handleAnswer(question.id, v)}
              >
                <div className="space-y-3">
                  {question.opciones?.map((opt) => (
                    <div key={opt.id} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-50">
                      <RadioGroupItem value={opt.id} id={opt.id} />
                      <Label htmlFor={opt.id} className="flex-1 cursor-pointer">{opt.texto}</Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            )}

            {/* Multiple choice (multiple) */}
            {question.tipo === 'multiple_answer' && (
              <div className="space-y-3">
                {question.opciones?.map((opt) => (
                  <div key={opt.id} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-50">
                    <Checkbox
                      id={opt.id}
                      checked={answers[question.id]?.respuestas_multiples?.includes(opt.id) || false}
                      onCheckedChange={() => handleAnswer(question.id, opt.id, true)}
                    />
                    <Label htmlFor={opt.id} className="flex-1 cursor-pointer">{opt.texto}</Label>
                  </div>
                ))}
              </div>
            )}

            {/* True/False */}
            {question.tipo === 'true_false' && (
              <RadioGroup
                value={answers[question.id]?.respuesta || ''}
                onValueChange={(v) => handleAnswer(question.id, v)}
              >
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-50">
                    <RadioGroupItem value="Verdadero" id="true" />
                    <Label htmlFor="true" className="flex-1 cursor-pointer">Verdadero</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-50">
                    <RadioGroupItem value="Falso" id="false" />
                    <Label htmlFor="false" className="flex-1 cursor-pointer">Falso</Label>
                  </div>
                </div>
              </RadioGroup>
            )}

            {/* Fill blank */}
            {question.tipo === 'fill_blank' && (
              <Input
                value={answers[question.id]?.respuesta || ''}
                onChange={(e) => handleAnswer(question.id, e.target.value)}
                placeholder="Escribe tu respuesta..."
              />
            )}

            {/* Short answer */}
            {question.tipo === 'short_answer' && (
              <Textarea
                value={answers[question.id]?.respuesta || ''}
                onChange={(e) => handleAnswer(question.id, e.target.value)}
                placeholder="Escribe tu respuesta..."
                rows={4}
              />
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            disabled={currentQuestion === 0}
            onClick={() => setCurrentQuestion(prev => prev - 1)}
          >
            Anterior
          </Button>
          
          <div className="flex gap-1">
            {questions.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentQuestion(i)}
                className={`w-8 h-8 rounded text-sm font-medium ${
                  i === currentQuestion
                    ? 'bg-[#004E89] text-white'
                    : answers[questions[i]?.id]
                      ? 'bg-green-100 text-green-700'
                      : 'bg-slate-100 text-slate-600'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          {currentQuestion < questions.length - 1 ? (
            <Button
              onClick={() => setCurrentQuestion(prev => prev + 1)}
              className="bg-[#004E89] hover:bg-[#003B66]"
            >
              Siguiente
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? 'Enviando...' : 'Enviar'}
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default QuizTake;
